import { ChangeDetectionStrategy, Component, input, Input, ViewEncapsulation } from "@angular/core";
import { S25Icons } from "./s25.icon.const";
import { TypeManagerDecorator } from "../../main/type.map.service";

@TypeManagerDecorator("s25-ng-icon")
@Component({
    selector: "s25-ng-icon",
    template: `
        @if (Icons[type] && !Icons[type].dataImage) {
            <svg
                class="icon {{ svgClass }}"
                [attr.role]="ariaLabel() === false ? null : 'img'"
                [attr.aria-hidden]="ariaLabel() === false ? 'true' : null"
                [style.width.px]="size"
                [style.height.px]="size"
                [attr.autoDarkMode]="autoDarkMode && Icons[type].hasDarkMode ? 'on' : 'off'"
                [attr.aria-label]="ariaLabel() === false ? null : ariaLabel() || null"
                xmlns="http://www.w3.org/2000/svg"
            >
                @if (ariaLabel() !== false) {
                    <title>{{ ariaLabel() || Icons[type].title }}</title>
                }
                <use
                    class="noPointerEvents light"
                    [attr.href]="'./resources/typescript/assets/images/sprite.svg#' + Icons[type].name"
                ></use>
                @if (Icons[type].hasDarkMode) {
                    <use
                        class="noPointerEvents dark"
                        [attr.href]="'./resources/typescript/assets/images/sprite.svg#' + Icons[type].name + '--dark'"
                    ></use>
                }
            </svg>
        } @else if (Icons[type]?.dataImage) {
            <div
                [style.background-image]="'url(' + Icons[type].dataImage + ')'"
                [style.height]="Icons[type].size.height"
                [style.width]="Icons[type].size.width"
                [attr.aria-hidden]="ariaLabel() === false ? 'true' : null"
                [attr.aria-label]="ariaLabel() === false ? null : ariaLabel() || null"
                class="dataImage"
            ></div>
        }
    `,
    styles: `
        @property --icon--rotation-degrees {
            syntax: "<angle>";
            initial-value: 0deg;
            inherits: true;
        }
        :host {
            display: inline-block;
        }

        .icon {
            display: inline-block;
            vertical-align: middle;
            height: var(--icon-size, 1.5em);
            width: var(--icon-size, 1.5em);
            transition: --icon--rotation-degrees 0.3s cubic-bezier(0.4, 0, 0.2, 1);
            transform: rotate(var(--icon--rotation-degrees, 0deg));
        }
        :host.s25-icon--rotating {
            .icon {
                transition: none;
            }
            .icon,
            .dataImage {
                animation: rotating 2s linear infinite;
            }
        }
        :host.s25-icon--inline {
            line-height: var(--icon-size, 1.5em);
        }

        .noPointerEvents {
            pointer-events: none;
        }

        .dark {
            display: none;
        }

        ::ng-deep .nm-party--on s25-ng-icon [autoDarkMode="on"] .light {
            display: none;
        }

        ::ng-deep .nm-party--on s25-ng-icon [autoDarkMode="on"] .dark {
            display: initial;
        }

        .dataImage {
            background-repeat: no-repeat;
            background-position: center;
        }

        @keyframes rotating {
            from {
                --icon--rotation-degrees: 0deg;
            }
            to {
                --icon--rotation-degrees: 360deg;
            }
        }
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.Emulated,
    standalone: true,
    host: {
        "[class.s25-icon--rotating]": "rotating",
        "[class.s25-icon--inline]": "inline()",
    },
})
export class S25IconComponent {
    @Input({ required: true }) type: S25Icons.Name;
    /**
     * Set ariaLabel to false if you want the icon to be PURELY DECORATIVE.
     * Ensure that a label is conveyed to screen readers by other means, e.g.
     * aria-label or text adjacent to the icon.
     */
    readonly ariaLabel = input.required<string | false | undefined>();
    @Input() rotating: boolean = false;
    @Input() size: number | string;
    @Input() autoDarkMode = false;
    @Input() svgClass: string = "";
    readonly inline = input(false);

    public Icons = S25Icons.untyped;
}
